import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  FlowInstanceService,
  INohsAuxiliaryService,
  INohsUploadService,
  NohsConsentResponse,
  NohsConsentsDTO,
  NohsFeedbackDTO,
  NohsFileUploadResponse,
  NohsFormSubmissionPayload,
  NohsPresentationDTO,
  NohsPreviousEntryDTO,
  NohsRecentEntryDTO,
  NohsSelectOption,
} from '@nohs/form-builder';
import { finalize, forkJoin, Observable } from 'rxjs';
import { API, STORAGE_KEYS } from '../app.constants';
import { LoadingService } from './loading.service';
import { StorageService } from './storage.service';

export interface getFlowInstanceOptions {
  flowId: number;
  activityId?: number;
  enterpriseId?: string;
  editInstanceId?: string;
  resultIds?: string;
  noRehydrate?: boolean;
  eventOwnerId?: number;
  eventOwnerTypeId?: number;
  sourceId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class FormsApiService implements INohsAuxiliaryService, INohsUploadService {
  private http = inject(HttpClient);
  private flowInstanceService = inject(FlowInstanceService);
  private loadingService = inject(LoadingService);
  private storageService = inject(StorageService);

  public getFlowInstance2(options: getFlowInstanceOptions) {
    let url = API.GET_FLOW_INSTANCE(options);
    url = this.appendIfExistsInOptions(url, options, 'activityId');
    url = this.appendIfExistsInOptions(url, options, 'enterpriseId');
    url = this.appendIfExistsInOptions(url, options, 'editInstanceId');
    url = this.appendIfExistsInOptions(url, options, 'resultIds');
    url = this.appendIfExistsInOptions(url, options, 'noRehydrate');
    url = this.appendIfExistsInOptions(url, options, 'eventOwnerId');
    url = this.appendIfExistsInOptions(url, options, 'eventOwnerTypeId');
    url = this.appendIfExistsInOptions(url, options, 'sourceId');
    return this._callWithLoaders(this.http.get<NohsPresentationDTO>(url), true);
  }

  private appendIfExistsInOptions(url: string, options: getFlowInstanceOptions, key: string) {
    if (options[key as keyof getFlowInstanceOptions] != null) {
      url += '&' + key + '=' + options[key as keyof getFlowInstanceOptions];
    }
    return url;
  }

  public submitForm(
    flowId: number,
    payload: NohsFormSubmissionPayload,
    activityId?: number
  ): Observable<NohsPresentationDTO> {
    const context = this.storageService.getItem(STORAGE_KEYS.CONTEXT_ID);
    return this._callWithLoaders(
      this.http.post<NohsPresentationDTO>(API.SUBMIT_FLOW({ flowId, activityId, context }), payload),
      true
    );
  }

  public navClick(
    flowId: number,
    navAction: string,
    payload: NohsFormSubmissionPayload,
    activityId?: number
  ): Observable<NohsPresentationDTO> {
    const context = this.storageService.getItem(STORAGE_KEYS.CONTEXT_ID);
    return this._callWithLoaders(
      this.http.post<NohsPresentationDTO>(API.NAV_CLICK({ flowId, navAction, activityId, context }), payload),
      true
    );
  }

  public recentEntries(endpointUrl: string): Observable<NohsRecentEntryDTO> {
    return this._callWithLoaders(this.http.get<NohsRecentEntryDTO>(API.RECENT_ENTRIES({ endpointUrl })), false);
  }

  public previousEntries(endpointUrl: string): Observable<NohsPreviousEntryDTO> {
    return this._callWithLoaders(
      this.http.get<NohsPreviousEntryDTO>(
        API.PREVIOUS_ENTRIES({ endpointUrl, activityId: this.flowInstanceService.activityId })
      ),
      false
    );
  }

  public deleteResult(resultIds: Array<number>): Observable<HttpResponse<string>> {
    const resultIdQS = 'resultId=' + resultIds.join('&resultId=');
    return this._callWithLoaders(
      this.http.delete(API.DELETE_RESULT({ resultIdQS }), { responseType: 'text', observe: 'response' }),
      false
    );
  }

  public feedbacks(endpointUrl: string, feedbackQueryString: string): Observable<Array<NohsFeedbackDTO>> {
    return this._callWithLoaders(
      this.http.get<Array<NohsFeedbackDTO>>(
        API.FEEDBACK({ endpointUrl, feedbackQueryString, instanceId: this.flowInstanceService.instanceId })
      ),
      false
    );
  }

  public fetchOptions(endpointUrl: string): Observable<Array<NohsSelectOption>> {
    return this._callWithLoaders(this.http.get<Array<NohsSelectOption>>(API.OPTIONS({ endpointUrl })), false);
  }

  public consents(endpointUrl: string): Observable<Array<NohsConsentsDTO>> {
    return this._callWithLoaders(this.http.get<Array<NohsConsentsDTO>>(API.CONSENTS({ endpointUrl })), false);
  }

  public resolveConsent(endpointUrl: string, userConsent: NohsConsentResponse): Observable<HttpResponse<object>> {
    return this._callWithLoaders(
      this.http.post(API.RESOLVE_CONSENT({ endpointUrl }), userConsent, { observe: 'response' }),
      false
    );
  }

  public uploadFile(files: FileList): Observable<NohsFileUploadResponse | Array<NohsFileUploadResponse>> {
    if (files.length === 1) {
      return this._callWithLoaders(this._uploadFile(files[0]), true);
    } else {
      return this._callWithLoaders(forkJoin(Array.from(files).map((file) => this._uploadFile(file))), true);
    }
  }

  private _uploadFile(file: File): Observable<NohsFileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<NohsFileUploadResponse>(
      API.UPLOAD_FILE({
        instanceId: this.flowInstanceService.instanceId,
        activityId: this.flowInstanceService.activityId,
      }),
      formData
    );
  }

  public getUploadedFiles(): Observable<Array<NohsFileUploadResponse>> {
    return this._callWithLoaders(
      this.http.get<Array<NohsFileUploadResponse>>(
        API.UPLOADED_FILES({ instanceId: this.flowInstanceService.instanceId })
      ),
      false
    );
  }

  private _callWithLoaders<T>(request: Observable<T>, blockingLoader: boolean): Observable<T> {
    this.loadingService.showSpinner(!blockingLoader);
    return request.pipe(
      finalize(() => {
        this.loadingService.hideSpinner(!blockingLoader);
      })
    );
  }
}
